/**
 * Eine Nutzer-Frage → eine Bot-Blase.
 *
 * Die getippte Antwort wird zweimal angeliefert: einmal als HTTP-Stream
 * (fillBotBubble + Modell-Vermerk) und einmal als persistChat → Bus → SSE
 * (appendBubble, nur Uhrzeit). Wer den zweiten Weg nicht gegen den ersten
 * hält, sieht „Hey. Da bin ich.“ zweimal — einmal mit „· Sonnet“, einmal
 * ohne. Genau das war 2312.
 *
 * Eine Regel: dieselbe Antwort, die in den letzten 90 Sekunden schon
 * sichtbar ist, wird nicht noch einmal gezeichnet. Vergleich über den
 * reinen Reply-Text (nicht DOM-textContent mit Uhrzeit/Avatar).
 */

const BOT_ANTWORT_FENSTER_MS = 90_000;

/** Markdown-agnostisch, damit Stream-Rohtext und gerenderte Blase matchen. */
export function normBotAntwort(text: string): string {
  return text
    .replace(/[*_~`#>]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Dieselbe Antwort — identisch, oder die eine ist der sichtbare Anfang
 * der anderen (Stream-Stub vs. persistierte Vollfassung).
 *
 * Unter 8 Zeichen kein Präfix: „Ok.“ darf eine spätere „Ok. Hier der Plan…“
 * nicht schlucken.
 */
export function istDieselbeBotAntwort(a: string, b: string): boolean {
  const x = normBotAntwort(a);
  const y = normBotAntwort(b);
  if (!x || !y) return false;
  if (x === y) return true;
  const kurz = x.length <= y.length ? x : y;
  const lang = x.length <= y.length ? y : x;
  if (kurz.length < 8) return false;
  return lang.startsWith(kurz);
}

type BotBlaseStand = {
  reply: string;
  ts: number;
};

/** Welche schon stehende Blase ist diese Antwort — oder keine. */
export function passendeBotBlase<T extends BotBlaseStand>(
  text: string,
  recent: T[],
  now = Date.now(),
  fensterMs = BOT_ANTWORT_FENSTER_MS,
): T | undefined {
  const n = normBotAntwort(text);
  if (!n) return undefined;
  return recent.find(
    (b) => now - b.ts <= fensterMs && istDieselbeBotAntwort(n, b.reply),
  );
}
